import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Container, Image} from '../model/layout.model';
import {EventBusService} from './event-bus.service';
import {EventBusTypes} from '../model/constants';
import {GrafikUmfang} from '../model/event-bus.model';

/**
 * "Höchstwerte übernehmen" (Oktober 2026): der Knopf im TV-Relevanz-Rahmen der Zeile
 * "HEUTE: Nachmittag" (NRW) bzw. "MORGEN: Nachmittag" (jede Lokalzeit-Stadt) kopiert die
 * Temperaturen der gleichen Karten aus der "ganz"-Zeile desselben Reiters – dort stehen die
 * Tageshöchstwerte – Box für Box in die Nachmittags-Karten.
 *
 * Nur die Temperatur einer Kombi-Box (`values[boxId]`), nie ihr Symbol (`boxId.icon`); Boxen,
 * deren Quelle leer ist, bleiben unangetastet. Geschrieben wird in `image.values` des
 * Dokumentbaums, schon gerenderte Karten erfahren davon über `GRAFIK_WERTE_GEAENDERT_EVENT`
 * (wie bei "für COSMO übernehmen").
 */
@Injectable({providedIn: 'root'})
export class HoechstwerteUebernahmeService {

  /** Zeilen-Segment der Ziel-Zeile → das der Quell-Zeile, je Bereich. */
  private static readonly QUELLEN: {praefix: RegExp; ziel: string; quelle: string}[] = [
    {praefix: /^wdr\.grafik\./, ziel: 'heute-nachmittag', quelle: 'heute-ganz'},
    {praefix: /^wdr\.lokalzeit\.[a-z-]+\./, ziel: 'morgen-nachmittag', quelle: 'morgen-ganz'}
  ];

  private static readonly ICON_SUFFIX = '.icon';

  constructor(private eventBus: EventBusService, private snackBar: MatSnackBar) {
  }

  /**
   * Die Quell-Karte ("ganz"-Zeile) zu einer Nachmittags-Karte – oder null, wenn die Karte
   * keinen Knopf bekommt. `wdr.grafik.heute-nachmittag.karte5` → `wdr.grafik.heute-ganz.karte5`,
   * `wdr.lokalzeit.koeln.morgen-nachmittag.karte3` → `wdr.lokalzeit.koeln.morgen-ganz.karte3`.
   */
  static quelleFuer(containerId: string | null | undefined): string | null {
    if (!containerId) {
      return null;
    }
    for (const q of HoechstwerteUebernahmeService.QUELLEN) {
      const praefix = containerId.match(q.praefix)?.[0];
      if (!praefix) {
        continue;
      }
      const rest = containerId.substring(praefix.length);           // "heute-nachmittag.karte5"
      const [zeile, ...karte] = rest.split('.');
      if (zeile === q.ziel && karte.length === 1) {
        return praefix + q.quelle + '.' + karte[0];
      }
    }
    return null;
  }

  /** Hat mindestens eine dieser Karten eine Quelle? Dann zeigt der Rahmen den Knopf. */
  static moeglich(karten: Image[]): boolean {
    return karten.some(k => HoechstwerteUebernahmeService.quelleFuer(k.containerId) !== null);
  }

  /**
   * Überträgt die Temperaturen der Quell-Karten (gesucht im `reiter`, dort liegt die
   * "ganz"-Zeile) in die Ziel-Karten und meldet das Ergebnis per Snackbar.
   *
   * @returns Zahl der übernommenen Werte
   */
  uebernehmen(reiter: Container | null | undefined, ziele: Image[]): number {
    let uebernommen = 0;
    const geaendert: string[] = [];
    for (const ziel of ziele) {
      const quellId = HoechstwerteUebernahmeService.quelleFuer(ziel.containerId);
      const quelle = quellId ? HoechstwerteUebernahmeService.finde(reiter, quellId) : null;
      if (!quelle) {
        continue;
      }
      const werte = {...(ziel.values ?? {})};
      let kopiert = 0;
      for (const box of ziel.boxes ?? []) {
        if (box.type !== 'tempicon' && box.type !== 'temperature') {
          continue;
        }
        const wert = quelle.values?.[box.id];
        if (wert === undefined || wert === null || wert === '') {
          continue;
        }
        werte[box.id] = wert;
        kopiert++;
      }
      if (kopiert > 0) {
        ziel.values = werte;
        uebernommen += kopiert;
        geaendert.push(ziel.containerId);
      }
    }
    if (geaendert.length > 0) {
      this.eventBus.emit(EventBusTypes.GRAFIK_WERTE_GEAENDERT_EVENT.valueOf(), new GrafikUmfang(geaendert));
      this.snackBar.open(`Höchstwerte übernommen: ${uebernommen} Temperaturen aus der Zeile "ganz".`, 'OK',
        {duration: 5000, panelClass: 'hinweis-erfolg'});
    } else {
      this.snackBar.open('Keine Höchstwerte übernommen: in der Zeile "ganz" sind keine Temperaturen eingetragen '
        + '(erst "MSWR-MOS laden" oder Werte eintippen).', 'OK', {duration: 7000, panelClass: 'hinweis-fehler'});
    }
    return uebernommen;
  }

  /** Sucht ein Bild im Teilbaum über seine containerId. */
  private static finde(container: Container | null | undefined, containerId: string): Image | null {
    for (const kind of container?.containerList ?? []) {
      if (kind.containerType === 'Image' && kind.containerId === containerId) {
        return kind as Image;
      }
      const gefunden = HoechstwerteUebernahmeService.finde(kind, containerId);
      if (gefunden) {
        return gefunden;
      }
    }
    return null;
  }
}
